import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const CashbackModal = ({ 
  isOpen, 
  onClose, 
  cashbackBalance, 
  currentSubtotal, 
  onApplyCashback,
  appliedCashback = 0
}) => {
  const { t } = useLanguage();
  const [cashbackAmount, setCashbackAmount] = useState(appliedCashback.toString());
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const maxCashback = Math.min(cashbackBalance, currentSubtotal);

  const handleApply = () => {
    const amount = parseFloat(cashbackAmount) || 0;
    
    if (amount <= 0) {
      setError(t('Please enter a valid cashback amount'));
      return;
    }
    
    if (amount > cashbackBalance) {
      setError(t('Amount cannot exceed your cashback balance of ₹{amount}', { amount: cashbackBalance }));
      return;
    }
    
    if (amount > currentSubtotal) {
      setError(t('Amount cannot exceed current subtotal of ₹{amount}', { amount: currentSubtotal.toFixed(2) }));
      return;
    }

    onApplyCashback(amount);
    onClose();
  };

  const handleClear = () => {
    onApplyCashback(0);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--white)',
        borderRadius: '12px',
        padding: '30px',
        width: '90%',
        maxWidth: '500px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            {t('Apply Cashback Discount')}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--gray-dark)'
            }}
          >
            ×
          </button>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #C6F6D5 0%, #9AE6B4 100%)',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '2px solid var(--success)'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
            <div>
              <div style={{ fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                {t('Available Cashback Balance')}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--success)' }}>
                ₹{cashbackBalance.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                {t('Current Invoice Subtotal')}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                ₹{currentSubtotal.toFixed(2)}
              </div>
            </div>
          </div>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            background: 'rgba(255, 255, 255, 0.7)', 
            borderRadius: '6px',
            fontSize: '12px',
            color: 'var(--dark-blue)'
          }}>
            <strong>{t('Maximum cashback you can apply')}:</strong> ₹{maxCashback.toFixed(2)}
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="cashbackAmount" className="form-label">
            {t('Cashback Amount to Apply')} (₹) *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max={maxCashback}
            id="cashbackAmount"
            className="form-input"
            value={cashbackAmount}
            onChange={(e) => {
              setCashbackAmount(e.target.value);
              setError('');
            }}
            placeholder={t('Max: ₹{amount}', { amount: maxCashback.toFixed(2) })}
            autoFocus
          />
          <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
            {t('Enter amount between ₹0.01 and ₹{amount}', { amount: maxCashback.toFixed(2) })}
          </div>
        </div>

        <div style={{ 
          background: '#FFF3CD',
          border: '1px solid #FFEAA7',
          padding: '15px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          <strong>{t('How it works')}:</strong>
          <ul style={{ marginTop: '8px', marginLeft: '20px', fontSize: '13px' }}>
            <li>{t('Cashback is applied as a discount to the invoice subtotal')}</li>
            <li>{t('GST will be recalculated on the reduced amount')}</li>
            <li>{t('This cashback will be deducted from your available balance')}</li>
            <li>{t('You can modify or remove the cashback before generating the invoice')}</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            {t('Cancel')}
          </button>
          {appliedCashback > 0 && (
            <button
              className="btn btn-secondary"
              onClick={handleClear}
              style={{ color: 'var(--error)' }}
            >
              {t('Clear Cashback')}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={handleApply}
            disabled={!cashbackAmount || parseFloat(cashbackAmount) <= 0}
            style={{ 
              background: 'var(--success)',
              border: 'none'
            }}
          >
            {t('Apply Cashback')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashbackModal;
